import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { collection, doc, getDoc, getDocs, orderBy, query, runTransaction, where } from "firebase/firestore";
import { db } from "@/firebase";
import { LoyaltyAccount, LoyaltyTransaction, LoyaltyTransactionType } from "@/types/loyalty";
import { sanitizePhone } from "@/utils/phones";
import { Content } from "@/types";
import { ApiError } from "@/types/api";

export const loyaltyApi = createApi({
  reducerPath: "loyaltyApi",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["LoyaltyAccount", "LoyaltyTransaction"],
  endpoints: (builder) => ({
    getLoyaltyAccount: builder.query<LoyaltyAccount | null, { organizationId: string; uid: string }>({
      queryFn: async ({ organizationId, uid }) => {
        try {
          const accountId = `${organizationId}_${uid}`;
          const docRef = doc(db, "loyaltyAccounts", accountId);
          const snapshot = await getDoc(docRef);

          if (snapshot.exists()) {
            const data = snapshot.data();
            return {
              data: {
                ...data,
                id: snapshot.id,
                joinedAt: data.joinedAt?.toMillis?.() || data.joinedAt,
                updatedAt: data.updatedAt?.toMillis?.() || data.updatedAt,
              } as LoyaltyAccount
            };
          }
          return { data: null };
        } catch (error) {
          return { error: error as any };
        }
      },
      providesTags: (_result, _error, { organizationId, uid }) => [{ type: "LoyaltyAccount", id: `${organizationId}_${uid}` }],
    }),
    getLoyaltyTransactions: builder.query<LoyaltyTransaction[], { organizationId: string; uid: string }>({
      queryFn: async ({ organizationId, uid }) => {
        try {
          const accountId = `${organizationId}_${uid}`;
          const colRef = collection(db, "loyaltyAccounts", accountId, "transactions");
          const q = query(colRef, orderBy("createdAt", "desc"));
          const snapshot = await getDocs(q);

          const transactions = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              ...data,
              id: doc.id,
              createdAt: data.createdAt?.toMillis?.() || data.createdAt,
            };
          }) as LoyaltyTransaction[];

          return { data: transactions };
        } catch (error) {
          return { error: error as any };
        }
      },
      providesTags: (result, _error) =>
        result
          ? [...result.map(({ id }) => ({ type: "LoyaltyTransaction" as const, id })), { type: "LoyaltyTransaction", id: "LIST" }]
          : [{ type: "LoyaltyTransaction", id: "LIST" }],
    }),
    addPoints: builder.mutation<void, { organizationId: string; uid: string; phoneNumber: string; points: number; subtotal: number; reference: string; createdBy: string }>({
      queryFn: async ({ organizationId, uid, phoneNumber, points, subtotal, reference, createdBy }) => {
        try {
          const accountId = `${organizationId}_${uid}`;
          const accountRef = doc(db, "loyaltyAccounts", accountId);
          const transactionsRef = collection(db, "loyaltyAccounts", accountId, "transactions");

          await runTransaction(db, async (transaction) => {
            const accountSnap = await transaction.get(accountRef);

            if (!accountSnap.exists()) {
              transaction.set(accountRef, {
                id: accountId,
                orgId: organizationId,
                uid,
                phoneNumber: sanitizePhone(phoneNumber),
                currentPoints: points,
                totalEarned: points,
                totalRedeemed: 0,
                joinedAt: Date.now(),
                updatedAt: Date.now(),
              });
            } else {
              const currentData = accountSnap.data() as LoyaltyAccount;

              transaction.update(accountRef, {
                currentPoints: (currentData.currentPoints || 0) + points,
                totalEarned: (currentData.totalEarned || 0) + points,
                updatedAt: Date.now(),
                lastUpdatedBy: createdBy,
                lastUpdatedAt: Date.now(),
              });
            }

            const newTxRef = doc(transactionsRef);

            transaction.set(newTxRef, {
              id: newTxRef.id,
              type: LoyaltyTransactionType.EARN,
              points,
              orderSubtotal: subtotal,
              reference: reference || "",
              createdBy,
              createdAt: Date.now(),
            });
          });

          return { data: undefined };
        } catch (error) {
          return { error: error as any };
        }
      },
      invalidatesTags: (_result, _error, { organizationId, uid }) => [
        { type: "LoyaltyAccount", id: `${organizationId}_${uid}` },
        { type: "LoyaltyTransaction", id: "LIST" }
      ],
    }),
    redeemPoints: builder.mutation<void, { organizationId: string; uid: string; contentId: string; rewardName: string; pointsCost: number; createdBy: string; branchId: string }>({
      queryFn: async ({ organizationId, uid, contentId, rewardName, pointsCost, createdBy, branchId }) => {
        try {
          const accountId = `${organizationId}_${uid}`;
          const accountRef = doc(db, "loyaltyAccounts", accountId);
          const transactionsRef = collection(db, "loyaltyAccounts", accountId, "transactions");

          await runTransaction(db, async (transaction) => {
            const accountSnap = await transaction.get(accountRef);

            if (!accountSnap.exists()) {
              throw new Error("La cuenta de lealtad no existe.");
            }

            const currentData = accountSnap.data() as LoyaltyAccount;
            const currentPoints = currentData.currentPoints || 0;

            if (currentPoints < pointsCost) {
              throw new Error("Puntos insuficientes.");
            }

            transaction.update(accountRef, {
              currentPoints: currentPoints - pointsCost,
              totalRedeemed: (currentData.totalRedeemed || 0) + pointsCost,
              updatedAt: Date.now(),
              lastUpdatedBy: createdBy,
              lastUpdatedAt: Date.now(),
            });

            const newTxRef = doc(transactionsRef);

            transaction.set(newTxRef, {
              id: newTxRef.id,
              type: LoyaltyTransactionType.REDEEM,
              points: pointsCost,
              contentId,
              rewardName,
              branchId,
              createdBy,
              createdAt: Date.now(),
            });
          });

          return { data: undefined };
        } catch (error) {
          return { error: error as any };
        }
      },
      invalidatesTags: (_result, _error, { organizationId, uid }) => [
        { type: "LoyaltyAccount", id: `${organizationId}_${uid}` },
        { type: "LoyaltyTransaction", id: "LIST" }
      ],
    }),
    fetchRedeemableContent: builder.query<Content[], { organizationId: string }>({
      queryFn: async ({ organizationId }) => {
        try {
          const collectionRef = collection(db, `organizations/${organizationId}/content`);
          const q = query(
            collectionRef,
            where("loyalty.isRedeemable", "==", true),
            where("status", "==", "published")
          );
          const snapshot = await getDocs(q);

          const content = snapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id
          })) as Content[];

          return { data: content };
        } catch (error) {
          return { error: error as any };
        }
      },
    }),
  }),
});

export const {
  useGetLoyaltyAccountQuery,
  useLazyGetLoyaltyAccountQuery,
  useGetLoyaltyTransactionsQuery,
  useLazyGetLoyaltyTransactionsQuery,
  useAddPointsMutation,
  useRedeemPointsMutation,
  useFetchRedeemableContentQuery,
  useLazyFetchRedeemableContentQuery
} = loyaltyApi;
